<template>
  <!--
    Studio · Reading Sessions — the per-beat simulator (right rail).

    Appears only when the operator clicks a beat's "Simulate" button. Auto-runs
    the lightweight, side-effect-free preview for that beat and streams the
    reader's response (local typewriter reveal). Lean by design — just the
    reader's face, the streamed reading, and a re-run.
  -->
  <aside class="beat-sim" :class="{ 'beat-sim--stacked': stacked }">
    <button v-if="!stacked" type="button" class="sim-close" @click="$emit('close')">Close <span aria-hidden="true">✕</span></button>
    <div class="sim-body" ref="bodyEl" aria-live="polite">
      <!-- Avatar/name lead the conversational beats; the card-reveal mirrors the
           live reading bubble, which has no avatar of its own. -->
      <header v-if="!isCardReveal" class="sim-head">
        <div class="sim-avatar-wrap">
          <img
            v-if="avatarUrl"
            :src="avatarUrl"
            :alt="readerName + ' avatar'"
            class="sim-avatar"
          />
          <div v-else class="sim-avatar placeholder">✦</div>
        </div>
        <span class="sim-reader-name">{{ readerName }}</span>
      </header>
      <p v-if="error" class="sim-error" role="alert">{{ error }}</p>
      <!-- Interview: a short playthrough that ends when the reader offers the
           cards — the offer point moves with the pace dial. -->
      <template v-else-if="isInterview && shownBubbles.length">
        <div
          v-for="(b, i) in shownBubbles" :key="i"
          class="sim-bubble"
          :class="['sim-bubble-' + b.role, { 'sim-bubble-offer': b.kind === 'invitation' }]"
        >
          <span v-if="b.role === 'seeker'" class="sim-bubble-who">Seeker</span>
          <span v-if="b.kind === 'invitation'" class="sim-bubble-tag">offers the cards</span>
          <p class="sim-bubble-text">{{ b.text }}</p>
        </div>
        <p v-if="loading" class="sim-output-loading">…</p>
      </template>
      <!-- Card selection: pick a face-down card → mirror the chat reveal. -->
      <template v-else-if="isCardReveal">
        <div v-if="!chosenCard" class="sim-cardpick">
          <p class="sim-cardpick-hint">Pick a card to see how it turns over.</p>
          <div class="sim-card-grid">
            <button
              v-for="c in cardPool" :key="c.card_id"
              type="button" class="sim-cardback"
              :aria-label="`Reveal a card`"
              @click="pickCard(c)"
            >
              <img
                v-if="cardBackImageUrl"
                :src="cardBackImageUrl" alt="" class="sim-cardback-img"
              />
              <span v-else aria-hidden="true">✦</span>
            </button>
          </div>
          <p v-if="loading && !cardPool.length" class="sim-output-loading">Shuffling…</p>
        </div>
        <div v-else class="sim-reveal">
          <!-- EXACT mirror of the live reading reveal bubble (ChatContainerReader
               `.card-message`): a centered title pill, then the card portrait
               beside the worded reveal. Kept in lockstep with the live styles. -->
          <div class="card-message">
            <div class="card-title">{{ revealPre
              }}<span v-if="revealSecondary" class="card-title-tarot"> ({{ revealSecondary }})</span>{{ revealTail }}</div>
            <div class="card-content">
              <img
                v-if="chosenCard.image_url"
                :src="chosenCard.image_url" :alt="chosenCard.card_name"
                class="card-image" :class="{ 'reversed-card': revealReversed }"
              />
              <div v-else class="card-image placeholder">✦</div>
              <div v-if="displayText" class="card-text-container card-caption">{{ displayText }}<span v-if="streaming" class="sim-caret">▍</span></div>
              <div v-else-if="loading" class="card-text-container card-caption is-loading">Turning the card…</div>
            </div>
          </div>
        </div>
      </template>
      <!-- The Reading: the real hand (deck at spread size) woven into one
           synthesis under the word cap — mirrors the live reading. -->
      <template v-else-if="isReading">
        <div v-if="readingCards.length" class="sim-reading-cards">
          <div v-for="(c, i) in readingCards" :key="i" class="sim-reading-card">
            <img v-if="c.image_url" :src="c.image_url" :alt="c.card_name" class="sim-reading-img" />
            <div v-else class="sim-reading-img placeholder">✦</div>
            <span class="sim-reading-pos">{{ c.position }}</span>
          </div>
        </div>
        <p v-if="displayText" class="sim-output-text">{{ displayText }}<span v-if="streaming" class="sim-caret">▍</span></p>
        <p v-else-if="loading" class="sim-output-loading">Weaving the reading…</p>
      </template>
      <!-- Image Direction: a REAL illustration, generated on demand (deliberate
           spend, cheapest settings) so it mirrors what the seeker sees. -->
      <template v-else-if="isImage">
        <img v-if="imageUrl" :src="imageUrl" alt="Illustration preview" class="sim-image" />
        <p v-if="imageLoading" class="sim-output-loading">Painting your vision…</p>
        <button
          v-else type="button" class="sim-generate"
          @click="generateImage"
        >{{ imageUrl ? '↻ Regenerate' : '✨ Generate preview' }}</button>
        <p v-if="!imageUrl && !imageLoading" class="sim-image-note">
          A quick, low-cost render of your brand + direction.
        </p>
      </template>
      <!-- Other beats: the one-shot streamed preview. -->
      <p v-else-if="displayText" class="sim-output-text">{{ displayText }}<span v-if="streaming" class="sim-caret">▍</span></p>
      <p v-else-if="loading" class="sim-output-loading">Reading…</p>
      <p v-else class="sim-output-empty">The reading appears here.</p>
    </div>
  </aside>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { store } from '@/store'
import {
  previewReadingBeat, runInterviewPlaythrough,
  getCardPool, runCardRevealPreview, runReadingPreview, runImagePreview,
} from '@/api-service'
import { parseAxiosError } from '@/utils/general-utils'

const props = defineProps({
  skillKey: { type: String, required: true },
  // { slug, label } of the beat being simulated
  beat: { type: Object, default: null },
  // The current (possibly-unsaved) moment text for the beat
  momentIntent: { type: String, default: '' },
  // The reader's cross-cutting style line ($reader_slant)
  readerSlant: { type: String, default: '' },
  // Response length cap for this beat (0 = no cap). Characters for most beats;
  // WORDS for card-reveal beats.
  maxChars: { type: Number, default: 0 },
  // Card-reveal meaning source ('symbolic' | 'generic').
  meaningSource: { type: String, default: 'symbolic' },
  // Increments on each Simulate / Re-Simulate click — drives (re)runs.
  runNonce: { type: Number, default: 0 },
  // When true the simulator renders as a self-contained chat-window card
  // (stacked below the beats) rather than a left-bordered side rail.
  stacked: { type: Boolean, default: false },
})

defineEmits(['close'])

const avatarUrl = computed(() => store.reader?.background_image || '')
const readerName = computed(() => store.reader?.name || 'Your reader')

// A representative seeker line per beat so the preview has something to react
// to. Sent silently — the operator doesn't manage it (kept off the surface to
// avoid clutter); the backend fills any blank anyway.
const SAMPLE_DEFAULTS = {
  grounding: "Hi... I'm not totally sure how this works, but I've got a lot on my mind.",
  interview: "I think I want to ask about my career, but it's tangled up with a relationship too.",
  reading: 'Should I take the new job offer?',
  image: 'Should I take the new job offer?',
}
function defaultSample(slug) {
  if (!slug) return ''
  return SAMPLE_DEFAULTS[slug] || 'Should I take the new job offer?'
}

const isInterview = computed(() => props.beat?.slug === 'interview')
const isReading = computed(() => props.beat?.slug === 'reading')
const isImage = computed(() => props.beat?.slug === 'image')
// Image preview is a deliberate spend — generated on button click, not auto-run.
const imageUrl = ref('')
const imageLoading = ref(false)
const isCardReveal = computed(() => {
  const s = props.beat?.slug || ''
  return s === 'card_reveal' || s.startsWith('card_reveal_')
})
// The real hand drawn for the reading preview (position + card image).
const readingCards = ref([])

// Card-selection simulator state.
// Same default card back the live game falls back to, so the simulator matches
// the reading even before the reader's own back loads.
const DEFAULT_CARD_BACK = 'https://mysticaidablob.blob.core.windows.net/public/reader_backgrounds/Master_Lee_4_oWt7iSD.png'
const cardPool = ref([])
const cardBackImageUrl = ref(DEFAULT_CARD_BACK)
const chosenCard = ref(null)
const revealPre = ref('')
// Canonical tarot name shown tiny when a Mystic's-Symbolism name headlines, and
// the composed tail (" reversed"/"" + "."). Kept separate so the FE can style
// the secondary name small.
const revealSecondary = ref('')
const revealTail = ref('')
const revealReversed = ref(false)

const loading = ref(false)
const error = ref('')
const displayText = ref('')
const streaming = ref(false)
let streamTimer = null

// Interview playthrough transcript + the count revealed so far (bubbles appear
// one at a time so the conversation plays out).
const transcript = ref([])
const shownCount = ref(0)
let bubbleTimer = null
const shownBubbles = computed(() => transcript.value.slice(0, shownCount.value))

// Auto-scroll the panel's own scroll area to the latest content so the Mystic
// never has to scroll the simulator by hand.
const bodyEl = ref(null)
function scrollToBottom() {
  nextTick(() => {
    if (bodyEl.value) bodyEl.value.scrollTop = bodyEl.value.scrollHeight
  })
}

function stopBubbles() {
  if (bubbleTimer) { clearInterval(bubbleTimer); bubbleTimer = null }
}

function stopStream() {
  if (streamTimer) { clearInterval(streamTimer); streamTimer = null }
  streaming.value = false
  stopBubbles()
}

// Local typewriter reveal — the endpoint returns the full beat text in one
// response; we reveal it progressively so it streams like a live reading.
function streamReveal(text) {
  stopStream()
  displayText.value = ''
  if (!text) return
  streaming.value = true
  let i = 0
  const step = Math.max(1, Math.round(text.length / 140))
  streamTimer = setInterval(() => {
    i += step
    displayText.value = text.slice(0, i)
    scrollToBottom()
    if (i >= text.length) { displayText.value = text; stopStream() }
  }, 18)
}

// Reveal the playthrough one bubble at a time so it plays like a conversation.
function revealTranscript(bubbles) {
  stopStream()
  transcript.value = bubbles
  shownCount.value = bubbles.length ? 1 : 0
  scrollToBottom()
  if (bubbles.length <= 1) return
  bubbleTimer = setInterval(() => {
    if (shownCount.value >= bubbles.length) { stopBubbles(); return }
    shownCount.value += 1
    scrollToBottom()
  }, 650)
}

async function runPreview() {
  if (!props.beat || loading.value) return
  loading.value = true
  error.value = ''
  stopStream()
  displayText.value = ''
  transcript.value = []
  shownCount.value = 0
  try {
    if (isCardReveal.value) {
      // Deal a fresh single row of face-down cards; the reveal runs on pick.
      chosenCard.value = null
      revealPre.value = ''
      const pool = await getCardPool(props.skillKey, 5)
      cardPool.value = pool.cards || []
      cardBackImageUrl.value = pool.card_back_image_url || DEFAULT_CARD_BACK
    } else if (isInterview.value) {
      const data = await runInterviewPlaythrough(props.skillKey, {
        moment_intent: props.momentIntent || '',
        reader_slant: props.readerSlant || '',
        max_chars: props.maxChars || 0,
      })
      revealTranscript((data && data.transcript) || [])
    } else if (isReading.value) {
      // Real multi-card reading: draw the reader's deck at spread size, weave it.
      readingCards.value = []
      const data = await runReadingPreview(props.skillKey, {
        moment_intent: props.momentIntent || '',
        reader_slant: props.readerSlant || '',
        max_words: props.maxChars || 0,
        meaning_source: props.meaningSource || 'symbolic',
      })
      readingCards.value = (data && data.cards) || []
      streamReveal((data && data.output) || '')
    } else if (isImage.value) {
      // Image is a deliberate spend — don't auto-generate; wait for the button.
      imageUrl.value = ''
    } else {
      const data = await previewReadingBeat(props.skillKey, props.beat.slug, {
        moment_intent: props.momentIntent || '',
        reader_slant: props.readerSlant || '',
        sample_input: defaultSample(props.beat.slug),
        max_chars: props.maxChars || 0,
      })
      streamReveal((data && data.output) || '')
    }
  } catch (e) {
    error.value = parseAxiosError(e)?.message || 'Preview failed. Try again.'
  } finally {
    loading.value = false
  }
}

// Generate a real illustration preview — a deliberate spend, fired by the
// button. Cheapest settings on the backend keep the burn down.
async function generateImage() {
  if (imageLoading.value) return
  imageLoading.value = true
  imageUrl.value = ''
  error.value = ''
  try {
    const data = await runImagePreview(props.skillKey, {
      image_direction: props.momentIntent || '',
      reader_slant: props.readerSlant || '',
      meaning_source: props.meaningSource || 'symbolic',
    })
    imageUrl.value = (data && data.image_url) || ''
  } catch (e) {
    error.value = parseAxiosError(e)?.message || 'Image generation failed. Try again.'
  } finally {
    imageLoading.value = false
  }
}

// Pick a face-down card → render exactly what the chat shows: the card image,
// the "you selected …" line, and the streamed N-word interpretation.
async function pickCard(card) {
  if (loading.value) return
  chosenCard.value = { ...card }
  revealPre.value = ''
  revealSecondary.value = ''
  revealTail.value = ''
  revealReversed.value = false
  loading.value = true
  error.value = ''
  stopStream()
  displayText.value = ''
  try {
    const data = await runCardRevealPreview(props.skillKey, {
      slug: props.beat.slug,
      card_id: card.card_id,
      moment_intent: props.momentIntent || '',
      reader_slant: props.readerSlant || '',
      max_words: props.maxChars || 0,
      meaning_source: props.meaningSource || 'symbolic',
    })
    revealPre.value = (data && data.pre_line) || ''
    revealSecondary.value = (data && data.tarot_name_secondary) || ''
    revealReversed.value = !!(data && data.reversed)
    revealTail.value = `${data && data.reversed ? ' reversed' : ''}.`
    if (data && data.card_image_url) chosenCard.value.image_url = data.card_image_url
    streamReveal((data && data.output) || '')
  } catch (e) {
    error.value = parseAxiosError(e)?.message || 'Reveal failed. Try again.'
  } finally {
    loading.value = false
  }
}
// Run on appear and on every Simulate / Re-Simulate click (the nonce changes
// each time, including when the same beat is re-simulated).
watch(() => props.runNonce, () => {
  stopStream()
  displayText.value = ''
  transcript.value = []
  shownCount.value = 0
  cardPool.value = []
  chosenCard.value = null
  revealPre.value = ''
  revealSecondary.value = ''
  revealTail.value = ''
  revealReversed.value = false
  readingCards.value = []
  imageUrl.value = ''
  error.value = ''
  runPreview()
}, { immediate: true })
onBeforeUnmount(stopStream)
</script>

<style scoped>
.beat-sim {
  position: relative;
  display: flex;
  flex-direction: column;
  /* No right inset — the chat window's right edge lines up with the top rule
     (canvas edge). */
  padding: 0 0 1rem 1rem;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  /* Bound the chat window so its bottom never drops below the bottom menu — it
     scrolls internally. Offset accounts for where the rail starts plus the nav. */
  max-height: calc(100dvh - 400px);
  box-sizing: border-box;
}
/* Stacked (Card Selection): no rail chrome — the light reveal bubble below is
   the whole show, exactly as the seeker sees it. Just leave room up top for the
   floating Close. Grows with the reveal (no internal scroll). */
.beat-sim--stacked {
  padding: 0;
  border-left: none;
  border: none;
  border-radius: 0;
  max-height: none;
  background: transparent;
}
.beat-sim--stacked .sim-reveal {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ── EXACT mirror of the live reading reveal (ChatContainerReader `.card-message`).
   Copied verbatim so the simulator and the reading read identically; if the live
   bubble ever changes, update both (or extract a shared component). ── */
.card-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f8f9fa;
  border: 2px solid #ddd;
  border-radius: 12px;
  padding: 10px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  /* soft blue halo to match the reading reveal's outer glow */
  box-shadow: 0 0 0 7px rgba(176, 199, 247, 0.30), 0 6px 14px rgba(0, 0, 0, 0.25);
  gap: 10px;
  box-sizing: border-box;
}
.card-title {
  text-align: center;
  margin: 0 0 5px 0;
  width: 100%;
  font-size: 1.05rem;
  color: #639;
  padding: 6px 10px;
  border: 1px solid #639;
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
}
.card-title-tarot { font-size: 0.85em; font-style: italic; opacity: 0.7; }
.card-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 15px;
  width: 100%;
  flex-wrap: nowrap;
}
.card-message .card-image {
  height: 180px;
  border-radius: 8px;
  object-fit: cover;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  flex: 0 0 auto;
}
.card-message .card-image.placeholder {
  display: flex; align-items: center; justify-content: center;
  width: 116px; color: #b9a0e8; font-size: 2rem;
  background: #efe9fb;
}
.card-message .reversed-card { transform: rotate(180deg); }
.card-text-container {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  text-align: center;
  width: 100%;
}
.card-caption {
  white-space: normal;
  font-size: 1.5rem;
  font-weight: 700;
  color: #663399 !important;
  margin: 0;
  text-align: center;
  text-transform: capitalize;
  line-height: 1.1;
  background-color: #f9f4ff;
  padding: 26px 14px;
  border-radius: 15px;
  border: 2px solid #663399;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
}
.card-caption.is-loading {
  font-size: 1rem; font-weight: 500; text-transform: none;
  color: #8a6fb0 !important; font-style: italic;
}
/* Avatar + name are the first thing inside the chat window and scroll up with
   the conversation (not pinned). */
.sim-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  text-align: center;
  margin-bottom: 0.7rem;
}
/* Pinned to the furthest top-right corner of the component — closes the panel. */
.sim-close {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.78rem;
  line-height: 1;
  cursor: pointer;
}
.sim-close span { font-size: 0.85rem; }
.sim-close:hover { color: #fff; border-color: rgba(255, 255, 255, 0.32); background: rgba(255, 255, 255, 0.06); }
.sim-avatar-wrap { flex: 0 0 auto; }
.sim-avatar {
  width: 56px; height: 56px; border-radius: 50%;
  object-fit: cover; border: 1px solid rgba(255, 255, 255, 0.15);
}
.sim-avatar.placeholder {
  display: flex; align-items: center; justify-content: center;
  background: rgba(255, 255, 255, 0.06); color: #c9b8ff; font-size: 1.3rem;
}
.sim-reader-name { font-weight: 600; font-size: 0.95rem; }
.sim-body {
  flex: 1 1 auto;
  min-height: 0;
  margin-top: 2.1rem;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 0.85rem;
}
.sim-output-text { margin: 0; font-size: 0.92rem; line-height: 1.6; white-space: pre-wrap; color: #E8D8FF; }
/* Interview playthrough bubbles. */
.sim-bubble {
  margin-bottom: 0.6rem;
  border-radius: 10px;
  padding: 0.55rem 0.7rem;
  animation: simfade 0.25s ease;
}
.sim-bubble-text { margin: 0; font-size: 0.88rem; line-height: 1.55; }
.sim-bubble-reader {
  background: rgba(201, 169, 78, 0.08);
  border: 1px solid rgba(201, 169, 78, 0.18);
  color: #E8D8FF;
}
.sim-bubble-seeker {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(232, 216, 255, 0.7);
  margin-left: 1.2rem;
}
.sim-bubble-who {
  display: block;
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.5;
  margin-bottom: 2px;
}
.sim-bubble-offer {
  background: rgba(201, 169, 78, 0.16);
  border-color: rgba(201, 169, 78, 0.5);
}
.sim-bubble-tag {
  display: inline-block;
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #C9A94E;
  margin-bottom: 3px;
}
@keyframes simfade { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
/* Card-selection simulator — face-down picker + chat-style reveal. */
.sim-cardpick-hint { margin: 0 0 0.7rem; font-size: 0.85rem; opacity: 0.7; text-align: center; }
/* One row of face-down cards (the reader's deck back). */
.sim-card-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.5rem;
}
.sim-cardback {
  aspect-ratio: 2 / 3;
  border-radius: 7px;
  border: 1px solid rgba(201, 169, 78, 0.4);
  background:
    radial-gradient(circle at 50% 30%, rgba(201,169,78,0.18), transparent 60%),
    linear-gradient(160deg, #1b1033, #0d0520);
  color: rgba(201, 169, 78, 0.8);
  font-size: 1.1rem;
  cursor: pointer;
  overflow: hidden;
  padding: 0;
  transition: transform 0.12s, border-color 0.12s;
}
.sim-cardback:hover { transform: translateY(-3px); border-color: #C9A94E; }
.sim-cardback-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.sim-reveal { display: flex; flex-direction: column; align-items: center; gap: 0.6rem; }
.sim-reveal-pre { margin: 0; font-size: 0.84rem; color: rgba(232, 216, 255, 0.75); text-align: center; }
/* Canonical tarot name shown small beside a Mystic's-Symbolism card name. */
.sim-reveal-tarot { font-size: 0.7rem; opacity: 0.6; font-style: italic; }
.sim-reveal-card { width: 58%; max-width: 200px; }
.sim-reveal-img {
  width: 100%; aspect-ratio: 2 / 3; object-fit: cover;
  border-radius: 10px; border: 1px solid rgba(255, 255, 255, 0.14);
}
.sim-reveal-img.placeholder {
  display: flex; align-items: center; justify-content: center;
  background: rgba(255, 255, 255, 0.05); color: #c9b8ff; font-size: 1.6rem;
}
.sim-reveal .sim-output-text { text-align: center; font-size: 0.95rem; }
/* The Reading preview: the drawn hand above the woven synthesis. */
.sim-reading-cards {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.7rem;
}
.sim-reading-card { display: flex; flex-direction: column; align-items: center; gap: 3px; }
.sim-reading-img {
  width: 54px;
  aspect-ratio: 2 / 3;
  object-fit: cover;
  border-radius: 5px;
  border: 1px solid rgba(201, 169, 78, 0.4);
}
.sim-reading-img.placeholder {
  display: flex; align-items: center; justify-content: center;
  color: rgba(201, 169, 78, 0.7); background: #1b1033;
}
.sim-reading-pos {
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(201, 169, 78, 0.75);
  max-width: 60px;
  text-align: center;
  line-height: 1.1;
}
/* Image Direction preview: the generated illustration + generate/regenerate. */
.sim-image { width: 100%; border-radius: 8px; border: 1px solid rgba(201, 169, 78, 0.3); display: block; margin-bottom: 0.5rem; }
.sim-generate {
  align-self: center;
  background: rgba(201, 169, 78, 0.14);
  border: 1px solid rgba(201, 169, 78, 0.45);
  color: #C9A94E;
  border-radius: 999px;
  padding: 0.4rem 1rem;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.15s;
}
.sim-generate:hover { background: rgba(201, 169, 78, 0.24); border-color: #C9A94E; }
.sim-image-note { margin: 0.4rem 0 0; font-size: 0.72rem; opacity: 0.5; font-style: italic; text-align: center; }
.sim-output-loading { margin: 0; font-size: 0.85rem; opacity: 0.55; font-style: italic; }
.sim-output-empty { margin: 0; font-size: 0.82rem; opacity: 0.4; font-style: italic; }
.sim-error { color: #ff9a9a; font-size: 0.85rem; margin: 0; }
.sim-caret { opacity: 0.6; animation: simblink 1s steps(2) infinite; }
@keyframes simblink { 50% { opacity: 0; } }
.sim-run-btn {
  align-self: flex-start;
  padding: 0.4rem 0.9rem; border-radius: 999px;
  border: 1px solid rgba(201, 184, 255, 0.4);
  background: rgba(201, 184, 255, 0.12); color: #e8e0ff;
  font-size: 0.85rem; cursor: pointer;
}
.sim-run-btn:hover:not(:disabled) { background: rgba(201, 184, 255, 0.2); }
.sim-run-btn:disabled { opacity: 0.5; cursor: default; }
</style>
